/**
 * Read only the delegated scope catalog of two fixed Microsoft resources.
 * https://learn.microsoft.com/en-us/graph/api/serviceprincipal-get?view=graph-rest-1.0
 * https://learn.microsoft.com/en-us/graph/api/resources/permissionscope?view=graph-rest-1.0
 * Catalog entries are NOT grants, effective rights, or endpoint requirements.
 */
import type { AuthProvider } from "../auth/types.js";
import { requestWithRetry } from "../utils/http.js";
import { ApiError } from "../utils/errors.js";
import { GUID_PATTERN } from "../utils/security.js";

export const COPILOT_PERMISSION_RESOURCES = {
  copilot_studio_management: "96ff4394-9197-43aa-b393-6a41652e21f8",
  power_platform_api: "8578e004-a5c6-46e7-913e-12f58912df43",
} as const;
export type CopilotPermissionResource = keyof typeof COPILOT_PERMISSION_RESOURCES;
const MAX_BYTES = 1024 * 1024;
const DEADLINE_MS = 15000;
const MAX_ROWS = 200;
const VALUE_PATTERN = /^[A-Za-z][A-Za-z0-9_.-]{0,119}$/;
type ObjectValue = Record<string, unknown>;
type FailureReason =
  | "invalid_input"
  | "auth_identity_unavailable"
  | "identity_changed"
  | "http_error"
  | "invalid_response"
  | "response_too_large"
  | "deadline_exceeded"
  | "read_unavailable";

export class CopilotPermissionCatalogError extends ApiError {
  constructor(
    public readonly reason: FailureReason,
    statusCode = 502,
  ) {
    super(
      `Copilot permission catalog: ${reason}${reason === "http_error" ? ` (HTTP ${statusCode})` : ""}. No fallback, sign-in, consent or permission change was attempted.`,
      statusCode,
    );
    this.name = "CopilotPermissionCatalogError";
  }
}
function fail(reason: FailureReason, statusCode = 502): never {
  throw new CopilotPermissionCatalogError(reason, statusCode);
}
function object(value: unknown): value is ObjectValue {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}
function guid(value: unknown): value is string {
  return typeof value === "string" && GUID_PATTERN.test(value);
}
function checkTree(value: unknown): void {
  const pending = [{ value, depth: 0 }];
  let nodes = 0;
  while (pending.length) {
    const item = pending.pop()!;
    if (++nodes > 10000 || item.depth > 12) fail("invalid_response");
    if (item.value !== null && typeof item.value === "object") {
      for (const [key, child] of Object.entries(item.value)) {
        if (["__proto__", "constructor", "prototype"].includes(key)) fail("invalid_response");
        pending.push({ value: child, depth: item.depth + 1 });
        if (pending.length > 10000) fail("invalid_response");
      }
    }
  }
}
function normalize(value: unknown, resource: CopilotPermissionResource) {
  checkTree(value);
  const appId = COPILOT_PERMISSION_RESOURCES[resource];
  if (
    !object(value) ||
    !guid(value.id) ||
    !guid(value.appId) ||
    value.appId.toLowerCase() !== appId
  )
    fail("invalid_response");
  const raw = value.oauth2PermissionScopes;
  const catalogState =
    raw === undefined
      ? "missing"
      : raw === null
        ? "null"
        : Array.isArray(raw)
          ? "present"
          : "invalid";
  const rows = Array.isArray(raw) ? (raw.slice(0, MAX_ROWS) as unknown[]) : null;
  const permissions: {
    id: string;
    value: string;
    isEnabled: boolean | null;
    type: "Admin" | "User" | null;
    missingFields: string[];
    invalidFields: string[];
  }[] = [];
  let invalidEntryCount = 0,
    duplicateEntryCount = 0;
  const ids = new Set<string>(),
    values = new Set<string>();
  for (const row of rows ?? []) {
    if (
      !object(row) ||
      !guid(row.id) ||
      typeof row.value !== "string" ||
      !VALUE_PATTERN.test(row.value)
    ) {
      invalidEntryCount++;
      continue;
    }
    const id = row.id.toLowerCase();
    if (ids.has(id) || values.has(row.value)) {
      duplicateEntryCount++;
      continue;
    }
    ids.add(id);
    values.add(row.value);
    const missingFields: string[] = [],
      invalidFields: string[] = [];
    const isEnabled = typeof row.isEnabled === "boolean" ? row.isEnabled : null;
    const type = row.type === "Admin" || row.type === "User" ? row.type : null;
    for (const key of ["isEnabled", "type"] as const) {
      if (row[key] == null) missingFields.push(key);
      else if ((key === "isEnabled" && isEnabled === null) || (key === "type" && type === null))
        invalidFields.push(key);
    }
    permissions.push({ id, value: row.value, isEnabled, type, missingFields, invalidFields });
  }
  const uninspectedEntryCount = Array.isArray(raw) ? Math.max(0, raw.length - MAX_ROWS) : null;
  const continuationPresent =
    Object.hasOwn(value, "@odata.nextLink") ||
    Object.hasOwn(value, "oauth2PermissionScopes@odata.nextLink");
  return {
    resource,
    resourceAppId: appId,
    servicePrincipalId: value.id.toLowerCase(),
    catalogState,
    permissions: rows === null ? null : permissions,
    receivedEntryCount: Array.isArray(raw) ? raw.length : null,
    inspectedEntryCount: rows?.length ?? null,
    returnedEntryCount: rows === null ? null : permissions.length,
    invalidEntryCount: rows === null ? null : invalidEntryCount,
    duplicateEntryCount: rows === null ? null : duplicateEntryCount,
    uninspectedEntryCount,
    truncated: (uninspectedEntryCount ?? 0) > 0 || continuationPresent,
    continuationPresent,
    interpretation:
      "Delegated permission definitions only; not granted permissions, effective rights, or proven endpoint requirements. No permissions were changed.",
  };
}

export class CopilotPermissionCatalogApi {
  constructor(private readonly authProvider: AuthProvider) {}

  async getPermissionCatalog(
    resource: CopilotPermissionResource,
    expectedTenantId: string,
    forceRefreshAuthentication = false,
  ) {
    if (
      typeof resource !== "string" ||
      !Object.hasOwn(COPILOT_PERMISSION_RESOURCES, resource) ||
      !guid(expectedTenantId) ||
      typeof forceRefreshAuthentication !== "boolean"
    )
      fail("invalid_input", 400);
    // A generic cached token getter cannot establish selected-account identity.
    const acquire = this.authProvider.getGraphTokenSilentForCurrentAccount?.bind(this.authProvider);
    if (!acquire) fail("auth_identity_unavailable", 401);
    const selected = this.authProvider.getCurrentAccount();
    if (
      !selected ||
      !guid(selected.tenantId) ||
      !guid(selected.localAccountId) ||
      selected.tenantId.toLowerCase() !== expectedTenantId.toLowerCase()
    )
      fail("auth_identity_unavailable", 401);
    const identity = { ...selected };
    const assertIdentity = () => {
      const now = this.authProvider.getCurrentAccount();
      if (
        !now ||
        now.tenantId !== identity.tenantId ||
        now.localAccountId !== identity.localAccountId ||
        now.username !== identity.username ||
        ("homeAccountId" in identity &&
          (!("homeAccountId" in now) || now.homeAccountId !== identity.homeAccountId)) ||
        ("environment" in identity &&
          (!("environment" in now) || now.environment !== identity.environment))
      )
        fail("identity_changed", 401);
    };
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let body: Awaited<ReturnType<typeof requestWithRetry>>["body"] | undefined;
    const assertActive = () => {
      if (controller.signal.aborted) fail("deadline_exceeded", 408);
    };
    const work = async () => {
      let token: string;
      try {
        token =
          forceRefreshAuthentication === true
            ? await acquire(expectedTenantId, { forceRefresh: true })
            : await acquire(expectedTenantId);
      } catch {
        fail("auth_identity_unavailable", 401);
      }
      assertActive();
      assertIdentity();
      if (
        typeof token !== "string" ||
        !token.trim() ||
        token.length > 65536 ||
        /[\r\n]/.test(token)
      )
        fail("auth_identity_unavailable", 401);
      const appId = COPILOT_PERMISSION_RESOURCES[resource];
      const response = await requestWithRetry(
        `https://graph.microsoft.com/v1.0/servicePrincipals(appId='${appId}')?$select=id,appId,oauth2PermissionScopes`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
          signal: controller.signal,
          headersTimeout: DEADLINE_MS,
          bodyTimeout: DEADLINE_MS,
        },
        { maxAttempts: 1 },
      );
      body = response.body;
      if (controller.signal.aborted) body.destroy();
      assertActive();
      assertIdentity();
      // Do not consume any error/redirect body or follow Location/nextLink values.
      if (response.statusCode !== 200) fail("http_error", response.statusCode);
      const contentType = response.headers["content-type"];
      if (
        typeof contentType !== "string" ||
        !/^application\/(?:json|[a-z0-9.+-]+\+json)(?:\s*;|$)/i.test(contentType)
      )
        fail("invalid_response");
      const chunks: Buffer[] = [];
      let size = 0;
      for await (const chunk of body as AsyncIterable<Uint8Array>) {
        assertActive();
        size += chunk.length;
        if (size > MAX_BYTES) fail("response_too_large", 413);
        chunks.push(Buffer.from(chunk));
      }
      assertActive();
      assertIdentity();
      let parsed: unknown;
      try {
        parsed = JSON.parse(Buffer.concat(chunks).toString("utf8"));
      } catch {
        fail("invalid_response");
      }
      return normalize(parsed, resource);
    };
    try {
      // Auth promises cannot be cancelled. The caller deadline still prevents late HTTP.
      return await Promise.race([
        work(),
        new Promise<never>((_, reject) => {
          timer = setTimeout(() => {
            controller.abort();
            body?.destroy();
            reject(new CopilotPermissionCatalogError("deadline_exceeded", 408));
          }, DEADLINE_MS);
        }),
      ]);
    } catch (error) {
      if (error instanceof CopilotPermissionCatalogError) throw error;
      fail("read_unavailable");
    } finally {
      clearTimeout(timer);
      controller.abort();
      body?.destroy();
    }
  }
}
